import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { avatarBg, getAvatarUrl, getInitials } from "~/utils/avatar";

export interface MemberOption {
  id: string;
  messengerNickname?: string | null;
  user?: {
    id: string;
    name: string;
    avatarUrl?: string | null;
    messengerAccounts?: Array<{ avatarUrl?: string | null }>;
  } | null;
}

interface MemberMultiSelectProps {
  /** Full team member list to pick from. */
  members: MemberOption[];
  /** Selected teamMember ids. */
  value: string[];
  onChange: (ids: string[]) => void;
  /** teamMember id to hide from the options (e.g. the project's assignee). */
  excludeId?: string;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Field height. "medium" = 52px (default, from the global Autocomplete
   * theme); "small" = 44px, matching the app's small input size.
   */
  size?: "small" | "medium";
}

function memberName(m: MemberOption): string {
  return m.messengerNickname ?? m.user?.name ?? "";
}

/**
 * Searchable multi-select for team members. Selected members render as
 * removable avatar chips that wrap to multiple lines. Mirrors the single
 * assignee picker's avatar/name treatment (see project modals).
 */
export default function MemberMultiSelect({
  members,
  value,
  onChange,
  excludeId,
  placeholder,
  disabled,
  size = "medium",
}: MemberMultiSelectProps) {
  const theme = useTheme();

  const options = members.filter((m) => m.id !== excludeId);
  const selected = value
    .map((id) => options.find((m) => m.id === id))
    .filter((m): m is MemberOption => Boolean(m));

  return (
    <Autocomplete
      multiple
      size={size}
      disableCloseOnSelect
      disableClearable
      openOnFocus
      // Figma has no field-level chevron/clear affordances — the only controls
      // are per-chip delete icons. Hide MUI's default popup + clear indicators.
      popupIcon={null}
      slotProps={{
        listbox: {
          sx: {
            "& .MuiAutocomplete-option:hover, & .MuiAutocomplete-option.Mui-focused": {
              bgcolor: `${theme.palette.custom.primarySecondaryHover} !important`,
            },
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              bgcolor: `${theme.palette.custom.primarySecondaryHover} !important`,
            },
            '& .MuiAutocomplete-option[aria-selected="true"]:hover': {
              bgcolor: `${theme.palette.custom.primarySecondaryPressed} !important`,
            },
          },
        },
      }}
      disabled={disabled}
      options={options}
      value={selected}
      onChange={(_, next) => onChange(next.map((m) => m.id))}
      getOptionLabel={memberName}
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      renderTags={(tags, getTagProps) =>
        (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "4px",
            }}
          >
            {tags.map((m, index) => {
              const name = memberName(m);
              const { key, ...chipProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...chipProps}
                  size="small"
                  avatar={
                    <Avatar
                      src={m.user ? getAvatarUrl(m.user) : undefined}
                      sx={{ bgcolor: avatarBg(name) }}
                    >
                      {getInitials(name)}
                    </Avatar>
                  }
                  label={name}
                  sx={{
                    height: 26,
                    m: 0,
                    px: "var(--Margin-Padding-3XS)",
                    gap: "var(--Margin-Padding-3XS)",
                    flexShrink: 0,
                    maxWidth: "none",
                    bgcolor: theme.palette.custom.gray[100],
                    border: `1px solid ${theme.palette.custom.gray[300]}`,
                    borderRadius: "6px",
                    "& .MuiChip-label": {
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      color: theme.palette.custom.gray[700],
                      px: 0,
                      overflow: "visible",
                      textOverflow: "clip",
                    },
                    "& .MuiChip-avatar": {
                      width: 20,
                      height: 20,
                      m: 0,
                      fontSize: 9,
                      fontWeight: 700,
                      color: theme.palette.custom.gray[0],
                    },
                    "& .MuiChip-deleteIcon": {
                      m: 0,
                      color: theme.palette.custom.gray[500],
                      "&:hover": {
                        color: theme.palette.custom.primaryHover,
                      },
                    },
                  }}
                />
              );
            })}
          </Box>
        )
      }
      renderOption={(props, m) => {
        const name = memberName(m);
        const { key, ...liProps } = props as typeof props & { key?: string };
        return (
          <li key={m.id} {...liProps}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar
                src={m.user ? getAvatarUrl(m.user) : undefined}
                sx={{
                  width: 20,
                  height: 20,
                  fontSize: 9,
                  fontWeight: 700,
                  bgcolor: avatarBg(name),
                }}
              >
                {getInitials(name)}
              </Avatar>
              <Typography variant="body2">{name}</Typography>
            </Stack>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={selected.length === 0 ? placeholder : undefined}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "0.5rem",
              fontSize: "0.875rem",
              bgcolor: theme.palette.custom.backgroundPaper,
              // Keep the chip row controlled here so MUI's default tag spacing
              // cannot override the intended layout.
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              alignContent: "center",
              // Field padding: 8px sides, 4px top/bottom.
              padding: "4px 8px",
              "& .MuiAutocomplete-tag": {
                margin: 0,
              },
              "& .MuiAutocomplete-input": {
                minWidth: 48,
                flexGrow: 1,
                padding: 0,
              },
              // Small size = 44px; overrides the global Autocomplete theme's
              // fixed 52px (which is set with !important).
              ...(size === "small" && { height: "44px !important" }),
              "& fieldset": { borderColor: theme.palette.custom.gray[300] },
              "&:hover fieldset": { borderColor: theme.palette.custom.primary },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.custom.primary,
                borderWidth: 2,
              },
            },
          }}
        />
      )}
    />
  );
}
